/* eslint-disable no-restricted-imports */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Input,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { IntegrationService, useCampaignNamesQuery } from 'src/api/queries';
import {
  campaignCount,
  matchesCampaignFilter,
  normalizeCampaignFilter,
} from 'src/features/Settings/utils/campaignFilter';
import { CampaignPreview } from './CampaignPreview';

type CampaignFilterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (campaignNameFilter: string | null) => void;
  currentFilter: string | null;
  isSaving: boolean;
  customer: string;
  service: IntegrationService;
  serviceLabel: string;
  hasAccountSelection: boolean;
};

export const CampaignFilterModal: React.FC<CampaignFilterModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentFilter,
  isSaving,
  customer,
  service,
  serviceLabel,
  hasAccountSelection,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isConfirmStep, setIsConfirmStep] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setInputValue(currentFilter ?? '');
      setIsConfirmStep(false);
    }
  }, [isOpen, currentFilter]);

  const normalizedValue = normalizeCampaignFilter(inputValue);
  const hasChanged = normalizedValue !== (currentFilter ?? null);

  const {
    data: campaignNamesData,
    isLoading: isLoadingCampaigns,
    isError: isCampaignsError,
  } = useCampaignNamesQuery({ customer, service }, isOpen);

  const campaigns = campaignNamesData?.campaigns;

  const matchingCampaigns = useMemo(() => {
    if (!campaigns) {
      return null;
    }
    return campaigns.filter((campaign) =>
      matchesCampaignFilter(campaign.name, normalizedValue),
    );
  }, [campaigns, normalizedValue]);

  const handleSaveClick = useCallback(() => {
    if (!hasChanged) {
      // Nothing changed - no destructive re-import needed
      onClose();
      return;
    }
    setIsConfirmStep(true);
  }, [hasChanged, onClose]);

  const handleConfirm = useCallback(() => {
    onSave(normalizedValue);
  }, [onSave, normalizedValue]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="16px" bg="surface.panel">
        <ModalCloseButton />
        <ModalHeader pb={2} pt={6}>
          <Text
            fontFamily="marketingPage"
            fontWeight="bold"
            fontSize="xl"
            color="content.title">
            Campaign name filter
          </Text>
        </ModalHeader>

        {isConfirmStep ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleConfirm();
            }}>
            <ModalBody px={6} py={4}>
              <Text fontSize="sm" fontFamily="marketingPage">
                {normalizedValue ? (
                  <>
                    Import only campaigns whose name starts with{' '}
                    <strong>{normalizedValue}</strong>
                    {matchingCampaigns && campaigns ? (
                      <>
                        {' '}
                        ({matchingCampaigns.length} of{' '}
                        {campaignCount(campaigns.length)})
                      </>
                    ) : null}
                    ?
                  </>
                ) : (
                  <>Remove the campaign name filter and import all campaigns?</>
                )}
              </Text>
              <Text
                mt={2}
                color="content.muted"
                fontSize="sm"
                fontFamily="marketingPage">
                Changing the filter removes previously imported {serviceLabel}{' '}
                data and re-imports matching campaigns. Campaign mappings that
                only involve excluded campaigns will be lost.
              </Text>
            </ModalBody>

            <ModalFooter gap={3} px={6} pb={6}>
              <Button
                bg="surface.sidebarMuted"
                color="content.title"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="back-campaign-filter"
                fontFamily="marketingPage"
                type="button"
                onClick={() => setIsConfirmStep(false)}
                flex={1}
                _hover={{ bg: 'sidebar.itemHoverBg' }}>
                Back
              </Button>
              <Button
                bg="background.red.500"
                color="white"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="confirm-campaign-filter"
                fontFamily="marketingPage"
                type="submit"
                isLoading={isSaving}
                flex={1}
                _hover={{ bg: 'red.600' }}>
                Confirm
              </Button>
            </ModalFooter>
          </form>
        ) : (
          <>
            <ModalBody px={6} py={4}>
              <Text
                fontSize="sm"
                color="content.muted"
                fontFamily="marketingPage"
                mb={3}>
                Import only campaigns whose names begin with this prefix. Keep
                it empty to import all campaigns.
              </Text>
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="e.g. US |"
                fontSize="sm"
                bg="surface.panel"
                border="1px solid"
                borderColor="surface.border"
                borderRadius="md"
                _placeholder={{ color: 'content.muted' }}
                _focus={{
                  borderColor: 'icon.green',
                  boxShadow: '0 0 0 1px var(--chakra-colors-icon-green)',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveClick();
                  }
                }}
                autoFocus
              />
              {!hasAccountSelection && (
                <Text
                  mt={3}
                  fontSize="sm"
                  color="orange.700"
                  fontFamily="marketingPage">
                  Showing campaigns from all accessible ad accounts — select ad
                  accounts to narrow the import.
                </Text>
              )}
              <CampaignPreview
                isLoading={isLoadingCampaigns}
                isError={isCampaignsError}
                campaigns={campaigns}
                matchingCampaigns={matchingCampaigns}
                filter={normalizedValue}
              />
            </ModalBody>

            <ModalFooter gap={3} px={6} pb={6}>
              <Button
                bg="surface.sidebarMuted"
                color="content.title"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="cancel-campaign-filter"
                fontFamily="marketingPage"
                onClick={onClose}
                flex={1}
                _hover={{ bg: 'sidebar.itemHoverBg' }}>
                Cancel
              </Button>
              <Button
                bg="icon.green"
                color="white"
                size="md"
                px={6}
                py={2}
                borderRadius="md"
                fontWeight="600"
                buttonName="save-campaign-filter"
                fontFamily="marketingPage"
                onClick={handleSaveClick}
                flex={1}
                _hover={{ bg: 'green.600' }}>
                Save
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
